<template>
  <div class="tech-board-container">
    <v-data-table
      :headers="tableHeaders"
      :items="tableData"
      :options="tableOptions"
      :loading="loading"
      item-key="key"
      hide-default-footer
      fixed-header
    >
      <template #[`item.memberName`]="{ item }">
        <router-link
          :to="`/member/${item.customerId}`"
          target="_blank"
        >
          {{ item.memberName }}
          <v-icon
            dark
            small
          >
            mdi-open-in-new
          </v-icon>
        </router-link>
      </template>
      <template #[`item.isVerified`]="{ item }">
        <td v-if="item.isVerified">
          <div class="verification-badge-container">
            <img
              src="@shared/assets/img/general/verified-badge.svg"
              alt="verified"
              class="verify-badge"
            >
            <div class="tooltip">
              <b>Verified Installation</b>
              <br>
              A Verified Unit is a SmartAC.com
              Hardware Kit that has passed these installation checks:
              <ol>
                <li>Do all installs have a pro user associated?</li>
                <li>Is the Hub online?</li>
                <li>Are all sensors online?</li>
                <li>Do Water Sensors in a drain pan have a water riser installed?</li>
                <li>Is the Comfort Sensor in an acceptable location?</li>
                <li>Has the nameplate photo been properly captured?</li>
                <li>Have the risk questions been answered?</li>
                <li>Is the connectivity on all devices acceptable?</li>
              </ol>
            </div>
          </div>
        </td>
        <td v-else-if="!item.lastVerifiedDate">
          <div class="verification-badge-container">
            <div class="pending-verification">
              Pending
            </div>
            <img
              class="tooltip-img"
              src="@shared/assets/img/adoption/pending-tooltip.svg"
            >
          </div>
        </td>
        <td v-else>
          <div class="verification-badge-container">
            <div class="first-reason-container">
              <div class="first-reason-pill">
                {{ parseReasons(item.failReasons)[0] }}
              </div>
              <div
                v-if="parseReasons(item.failReasons).length - 1 > 0"
                class="more-reasons-indicator"
              >
                +{{ parseReasons(item.failReasons).length - 1 }}
              </div>
            </div>
            <div
              v-if="item.failReasons"
              class="tooltip-unverified"
            >
              <b>Unverified</b>
              <div class="tooltip-unverified-pill-container">
                <div
                  v-for="(r, idx) in parseReasons(item.failReasons)"
                  :key="idx"
                  class="tooltip-unverified-pill"
                >
                  {{ r }}
                </div>
              </div>
            </div>
          </div>
        </td>
      </template>
      <template #[`item.address`]="{ item }">
        <td>
          {{ item.address }}
        </td>
      </template>
      <template #[`item.profileCompletedDate`]="{ item }">
        <td>
          {{ item.profileCompletedDate ? 'Profiled' : 'Not Profiled' }}
        </td>
      </template>
      <template #[`item.creationDate`]="{ item }">
        <td>
          {{ dateWithoutHour(item.creationDate, dateFormat) }}
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>

import formatterMixin from '@shared/mixins/formatterMixin';
import technicianMixin from '@shared/mixins/technicianMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';

export default {
  name: 'TechnicianBoard',
  mixins: [formatterMixin, technicianMixin, unitPreferencesMixin],
  props: {
    installations: Array,
    search: String,
    loading: Boolean,
  },
  data() {
    return {
      tableOptions: {
        page: 1,
        itemsPerPage: -1,
        sortBy: ['creationDate'],
        sortDesc: [true],
      },
      tableHeaders: [{
        text: 'MEMBER',
        align: 'start',
        sortable: true,
        filterable: false,
        value: 'memberName',
      },
      {
        text: 'VERIFICATION STATUS',
        align: 'start',
        sortable: false,
        filterable: false,
        value: 'isVerified',
      },
      {
        text: 'ADDRESS',
        align: 'start',
        sortable: true,
        filterable: false,
        value: 'address',
      },
      {
        text: 'NAMEPLATE',
        align: 'start',
        sortable: true,
        filterable: false,
        value: 'profileCompletedDate',
      },
      {
        text: 'DATE',
        align: 'start',
        sortable: true,
        filterable: false,
        value: 'creationDate',
      }],
    };
  },
  computed: {
    tableData() {
      let filtered = this.installations;
      if (this.search) {
        filtered = this.installations.filter((installation) => {
          const {
            memberName,
            address,
          } = installation;
          const searchTerm = this.search.toLowerCase();
          if (memberName && memberName.toLowerCase().includes(searchTerm)) {
            return true;
          }
          return !!(address && address.toLowerCase().includes(searchTerm));
        });
      }
      return filtered;
    },
  },
  methods: {
    parseReasons(reasons) {
      let splitChar = '\n';
      if (!reasons) {
        return reasons;
      }
      if (reasons.includes(';')) {
        splitChar = ';';
      }
      return reasons.split(splitChar).filter((c) => c.trim().length > 0);
    },
  },
};
</script>

<style lang="scss">

.tech-board-container {
  width: 100%;
  margin-top: 34px;
  padding: 0 0 12px 8px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: $N95;

  .v-data-table__wrapper {
    background-color: $N95;
    height: 700px;

    table {
      border-collapse: separate !important;
      border-spacing: 0 8px !important;

      thead {
        th {
          height: 30px !important;
          background: $N95 !important;

          span {
            color: $WHITE;
          }

          .v-icon {
            color: $WHITE !important;
          }

          &:last-child {
            text-align: end !important;
          }
        }

        .v-data-table__progress {
          th {
            height: 1px !important;
          }
        }
      }

      tbody {
        tr {
          td {
            background: $N90 !important;
            color: $WHITE;
            font-weight: 600;

            &:first-child {
              border-top-left-radius: 8px;
              border-bottom-left-radius: 8px;
            }

            &:last-child {
              border-top-right-radius: 8px;
              border-bottom-right-radius: 8px;
              text-align: right;
            }

            &:only-child {
              text-align: center;
            }

            a {
              color: $WHITE;
              text-decoration: underline;
            }
          }
        }
      }
    }

    .verify-badge {
      width: 24px;
      height: 24px;
      cursor: pointer;
      &:hover + .tooltip {
        display: flex;
        flex-direction: column;
      }
    }

    .verification-badge-container {
      position: relative;
      &:hover {
        .tooltip-img {
          display: flex;
        }
      }
      .pending-verification {
        font-size: 14px;
        font-weight: 600;
        color: $WHITE;
      }
    }

    .tooltip-unverified {
      position: absolute;
      padding: 10px;
      width: 316px;
      background-color: $WHITE;
      color: $BLACK;
      border-radius: 8px;
      align-items: flex-start;
      justify-content: center;
      box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
      z-index: 1;
      top: 40px;
      display: none;
      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        width: 0;
        height: 0;
        border: 8px solid transparent;
        border-bottom-color: $WHITE;
        border-top: 0;
        margin-left: -8px;
        margin-top: -8px;
      }
    }

    .tooltip-unverified-pill-container {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      height: 100%;

      .tooltip-unverified-pill {
        padding: 5px;
        border-radius: 25px;
        margin-right: 5px;
        background-color: $SUNSET100;
        margin-bottom: 10px;
      }
    }

    .first-reason-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: fit-content;
      cursor: pointer;
      &:hover + .tooltip-unverified {
        display: flex;
        flex-direction: column;
      }
    }

    .first-reason-pill {
      padding: 5px;
      border-radius: 25px;
      margin-right: 10px;
      background-color: $SUNSET100;
      color: $BLACK;
      white-space: nowrap;
    }

    .more-reasons-indicator {
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: $WHITE;
      border-radius: 12px;
      color: $BLACK;
      font-size: 12px;
      font-weight: 500;
      height: 25px;
      width: 35px;
      cursor: pointer;
    }

    .tooltip {
      position: absolute;
      width: 350px;
      background-color: $SUNSET100;
      color: $BLACK;
      align-items: flex-start;
      justify-content: center;
      border-radius: 4px;
      box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
      padding: 10px 10px;
      z-index: 1;
      top: 28px;
      left: -40px;
      display: none;
    }

    .tooltip-img {
      position: absolute;
      width: 320px;
      height: 356px;
      border-radius: 4px;
      box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
      padding: 10px 10px;
      z-index: 1;
      top: 28px;
      left: -40px;
      display: none;
    }
  }
}
</style>
